import { SelectField } from '@/components/enc/fields';
import { coupleInitials, coupleName, eventShort, phoneBR } from '@/components/enc/format';
import { EditIconButton, GridTable } from '@/components/enc/grid-table';
import {
  InitialsAvatar,
  PageHeader,
  SacramentTags,
  StatusPill,
} from '@/components/enc/primitives';
import { Button } from '@/components/ui/button';
import { ContentLoader } from '@/components/ui/loaders';
import { useToast } from '@/hooks/use-toast';
import { SacramentStatuses } from '@/lib/selectors/sacrament-status';
import { cn } from '@/lib/utils';
import { useCouples } from '@/services/couples';
import { useEvent, useEvents } from '@/services/events';
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import type { Couple, Event } from 'generated/prisma/browser';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

type Model = Couple & { event?: Event };

const PER_PAGE = 10;

const CHIPS = [
  ['Todos', (c: Model) => c.isActive],
  ['Casados na Igreja', (c: Model) => c.isActive && c.marriageSacrament],
  ['Ainda não casados', (c: Model) => c.isActive && !c.marriageSacrament],
  ['Sem crisma', (c: Model) => c.isActive && c.confirmationStatus !== 'BOTH'],
  ['Inativos', (c: Model) => !c.isActive],
] as const;

type Chip = (typeof CHIPS)[number][0];

const normalize = (v: string) => v.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const matchesQuery = (c: Model, q: string) => {
  const digits = q.replace(/\D/g, '');
  const text = normalize([c.husbandName, c.wifeName, c.email, c.parish ?? ''].join(' '));
  if (text.includes(normalize(q))) return true;
  return digits.length >= 3 && `${c.husbandPhone} ${c.wifePhone}`.includes(digits);
};

function exportCsv(couples: Model[]) {
  const head = [
    'Esposo',
    'Telefone do esposo',
    'Esposa',
    'Telefone da esposa',
    'E-mail',
    'CEP',
    'Endereço',
    'Número',
    'Paróquia',
    'Pastoral / Movimento',
    'Batismo',
    'Crisma',
    'Casados na Igreja',
    'Filhos',
    'Encontro',
  ];
  const rows = couples.map((c) => [
    c.husbandName,
    phoneBR(c.husbandPhone),
    c.wifeName,
    phoneBR(c.wifePhone),
    c.email,
    c.cep,
    c.address,
    c.addressNumber,
    c.parish,
    c.pastoralMovement,
    SacramentStatuses[c.baptismStatus],
    SacramentStatuses[c.confirmationStatus],
    c.marriageSacrament ? 'Sim' : 'Não',
    c.childrenInfo,
    eventShort(c.event?.description),
  ]);
  const csv = [head, ...rows]
    .map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';'))
    .join('\n');

  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
  a.download = 'casais-encheivos.csv';
  a.click();
  URL.revokeObjectURL(a.href);
}

export const Page = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const query = params.get('q')?.trim() ?? '';

  const { data: nextEvent, isLoading: loadingNext } = useEvent('next');
  const { data: events } = useEvents({
    perPage: 100,
    orderBy: 'startDate',
    order: 'desc',
  });

  // Com busca, a lista cobre todos os encontros; sem busca, o padrão é o próximo encontro.
  const eventParam = params.get('eventId');
  const eventFilter = eventParam ?? (query ? 'all' : (nextEvent?.id ?? 'all'));

  const { data, isLoading } = useCouples({
    perPage: 5000,
    orderBy: 'createdAt',
    order: 'desc',
    ...(eventFilter !== 'all' && { eventId: eventFilter }),
  });

  const [chip, setChip] = useState<Chip>('Todos');

  const byQuery = useMemo(
    () => (data?.data ?? []).filter((c) => !query || matchesQuery(c, query)),
    [data, query],
  );
  const chipTest = CHIPS.find(([label]) => label === chip)![1];
  const filtered = useMemo(() => byQuery.filter(chipTest), [byQuery, chipTest]);

  const setEvent = (value: string) => {
    const next = new URLSearchParams(params);
    next.set('eventId', value);
    setParams(next, { replace: true });
  };

  const handleEdit = (id: string) => navigate(`/admin/couples/${id}/edit`);

  const columns = useMemo<ColumnDef<Model>[]>(
    () => [
      {
        id: 'couple',
        header: 'CASAL',
        cell: ({ row: { original: c } }) => (
          <div
            onClick={() => handleEdit(c.id)}
            className="flex min-w-0 cursor-pointer items-center gap-3"
          >
            <InitialsAvatar text={coupleInitials(c)} />
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-sm font-semibold text-ink">
                {coupleName(c)}
              </span>
              <span className="truncate text-xs text-muted">{c.email}</span>
            </span>
          </div>
        ),
      },
      {
        id: 'contact',
        header: 'CONTATO',
        cell: ({ row: { original: c } }) => (
          <div className="flex flex-col gap-0.5 text-[13px] text-body">
            <span>{phoneBR(c.husbandPhone)}</span>
            <span className="text-muted">{phoneBR(c.wifePhone)}</span>
          </div>
        ),
      },
      {
        id: 'parish',
        header: 'PARÓQUIA',
        cell: ({ row: { original: c } }) => (
          <span className="block truncate text-[13px] text-body">
            {c.parish || 'Não informada'}
          </span>
        ),
      },
      {
        id: 'sacraments',
        header: 'SACRAMENTOS',
        cell: ({ row: { original: c } }) => <SacramentTags couple={c} />,
      },
      {
        id: 'event',
        header: 'ENCONTRO',
        cell: ({ row: { original: c } }) => (
          <span className="text-[13px] text-body">
            {eventShort(c.event?.description)}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'STATUS',
        cell: ({ row: { original: c } }) => <StatusPill active={c.isActive} />,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row: { original: c } }) => (
          <EditIconButton onClick={() => handleEdit(c.id)} />
        ),
      },
    ],
    [],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageIndex: 0, pageSize: PER_PAGE } },
  });

  useEffect(() => table.setPageIndex(0), [chip, query, eventFilter]);

  const { pageIndex } = table.getState().pagination;
  const pageCount = Math.max(1, table.getPageCount());
  const first = pageIndex * PER_PAGE;
  const shown = table.getRowModel().rows.length;

  const eventOptions = [
    { value: 'all', label: 'Todos os encontros' },
    ...(events?.data ?? []).map((e) => ({ value: e.id, label: e.description })),
  ];

  return (
    <div className="flex flex-col gap-5">
      <ContentLoader show={isLoading || loadingNext} />

      <PageHeader
        eyebrow="CADASTROS"
        title="Casais"
        description="Gerencie os casais inscritos em todos os encontros."
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => {
                exportCsv(filtered);
                toast.success(`${filtered.length} casais exportados.`);
              }}
            >
              Exportar CSV
            </Button>
            <Button onClick={() => navigate('/admin/couples/add')}>Novo casal</Button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <SelectField
          aria-label="Encontro"
          wrapperClassName="w-[260px] max-w-full"
          className="h-11"
          value={eventFilter}
          onChange={(e) => setEvent(e.target.value)}
          options={eventOptions}
        />
        <div className="flex flex-wrap gap-2">
          {CHIPS.map(([label, test]) => {
            const on = chip === label;
            return (
              <button
                key={label}
                type="button"
                onClick={() => setChip(label)}
                className={cn(
                  'flex h-9 items-center gap-2 rounded-full border px-3.5 text-[13px] font-semibold transition-colors',
                  on
                    ? 'border-brand-fill bg-brand-fill text-white'
                    : 'border-line-2 bg-transparent text-body hover:border-brand/50',
                )}
              >
                {label}
                <span className="text-[11px] opacity-75">
                  {byQuery.filter(test).length}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <GridTable
        table={table}
        minWidth={980}
        columns="minmax(0,2.2fr) minmax(0,1.7fr) minmax(0,1.5fr) minmax(0,1fr) minmax(0,1.1fr) minmax(0,.8fr) 40px"
        empty={!isLoading && 'Nenhum casal encontrado com esses filtros.'}
        footer={
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3.5">
            <span className="text-[11px] font-bold tracking-[.09em] text-muted">
              {filtered.length
                ? `EXIBINDO ${first + 1}–${first + shown} DE ${filtered.length} CASAIS`
                : 'NENHUM RESULTADO'}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="Página anterior"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="flex size-9 items-center justify-center rounded-[10px] border border-line-2 text-ink disabled:opacity-40"
              >
                <ChevronLeft className="size-4" />
              </button>
              <span className="min-w-[60px] text-center text-[13px] font-semibold text-body">
                {pageIndex + 1} de {pageCount}
              </span>
              <button
                type="button"
                aria-label="Próxima página"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="flex size-9 items-center justify-center rounded-[10px] border border-line-2 text-ink disabled:opacity-40"
              >
                <ChevronRight className="size-4" />
              </button>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default Page;
